const E2B_STREAM_HOST_RE = /^\d+-[a-z0-9]+(?:-[a-z0-9]+)*\.e2b\.(?:app|dev)$/;
const KERNEL_STREAM_DOMAINS = ['onkernel.com', 'kernel.sh'];
const KERNEL_STREAM_PORTS = new Set(['', '443', '8443']);
const KERNEL_READ_ONLY_VALUES = new Set(['true', 'false']);
const ALLOWED_QUERY_KEYS = new Set([
  'autoconnect',
  'view_only',
  'resize',
  'password',
]);

export const BROWSER_STREAM_IFRAME_SANDBOX = 'allow-scripts allow-same-origin';

const hasExplicitPort = (url: string): boolean => {
  const authority = url.slice('https://'.length).split(/[/?#]/, 1)[0] ?? '';
  return authority.includes('@') || authority.includes(':');
};

const isSafeE2bBrowserStreamUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length === 0) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.hash ||
    url.pathname !== '/vnc.html' ||
    hasExplicitPort(value) ||
    !E2B_STREAM_HOST_RE.test(url.hostname)
  ) {
    return false;
  }
  const keys = [...new Set(url.searchParams.keys())];
  if (keys.some((key) => !ALLOWED_QUERY_KEYS.has(key))) return false;
  if (url.searchParams.getAll('autoconnect').join('\0') !== 'true') {
    return false;
  }
  if (url.searchParams.getAll('resize').join('\0') !== 'scale') return false;
  const passwords = url.searchParams.getAll('password');
  if (passwords.length !== 1 || passwords[0] === '') return false;
  return (
    !url.searchParams.has('view_only') ||
    url.searchParams.getAll('view_only').join('\0') === 'true'
  );
};

const isSafeKernelBrowserStreamUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length === 0) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  const safeHostname = KERNEL_STREAM_DOMAINS.some(
    (domain) => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
  );
  return (
    url.protocol === 'https:' &&
    !url.username &&
    !url.password &&
    !url.hash &&
    safeHostname &&
    KERNEL_STREAM_PORTS.has(url.port) &&
    KERNEL_READ_ONLY_VALUES.has(url.searchParams.getAll('readOnly').join('\0'))
  );
};

export const isSafeBrowserStreamUrl = (value: unknown): value is string =>
  isSafeE2bBrowserStreamUrl(value) || isSafeKernelBrowserStreamUrl(value);

// Both providers serve an input-capable stream; the renderer gates input locally
export const getInteractiveBrowserStreamUrl = (
  value: unknown
): string | undefined => {
  if (isSafeE2bBrowserStreamUrl(value)) {
    const url = new URL(value);
    url.searchParams.delete('view_only');
    return url.href;
  }
  if (isSafeKernelBrowserStreamUrl(value)) {
    const url = new URL(value);
    url.searchParams.set('readOnly', 'false');
    return url.href;
  }
  return undefined;
};
